import React from 'react';
import { ValidationError } from 'yup';
import { IBadRequestError } from '@hooks/useMessage';
import { IField } from '@hooks/useField';
import { AxiosResponse } from 'axios';
import { useEffectOnce } from './useEffectOnce';
import { TransformValue } from '@utils/TypeHelpers';

export type FormFields<TForm extends object> = TransformValue<TForm, IField>;

export interface IForm<TForm extends object> {
    fields: Required<FormFields<TForm>>

    setValues: (values: TForm) => void
    getValues: () => TForm
    clearValues: VoidFunction
    saveValues: VoidFunction
    handleResponseSetValues: (response: AxiosResponse<TForm>) => void

    setErrorsFromYup: (e: any) => void
    setErrorsFromServer: (errors: IBadRequestError[]) => void
    clearErrors: VoidFunction

    reset: VoidFunction
    clear: VoidFunction

    getInputValues: () => Required<TForm>
    forEachField: (callback: (field: IField, fieldKeyOf: keyof TForm) => void) => void
}

export function useForm<TForm extends object>(fields: FormFields<TForm>) : IForm<TForm> {
    const fieldKeys = React.useMemo(() => Object.keys(fields), [fields]);
    const fieldKeysLength = React.useMemo(() => fieldKeys.length, [fieldKeys]);

    const forEachField = React.useCallback((callback: (field: IField, fieldKeyOf: keyof TForm) => void) => {
        for (let i = 0; i < fieldKeysLength; i++) {
            const fieldKey = fieldKeys[i];
            const fieldKeyOf = fieldKey as keyof TForm;
            const field = fields[fieldKeyOf];
            callback(field, fieldKeyOf);
        }
    }, [fieldKeys, fieldKeysLength, fields]);

    const setValues = React.useCallback((values: TForm) => {
        forEachField((field, fieldKeyOf) => {
            const value = values[fieldKeyOf];
            field.setValue(value);
        });
    }, [forEachField]);

    const getValues = React.useCallback(() => {
        const values = {} as TForm;

        forEachField((field, fieldKeyOf) => {
            const value = field.value;
            values[fieldKeyOf] = value;
        });

        return values;
    }, [forEachField]);

    const getInputValues = React.useCallback(() => {
        const values = {} as Required<TForm>;
        
        forEachField((field, fieldKeyOf) => {
            const value = field.getInputValue();
            values[fieldKeyOf] = value;
        })

        return values;

    }, [forEachField]);

    const setErrorsFromYup = React.useCallback((e: any) => {
        const errors = (e as ValidationError || {}).inner || [];

        if (errors.length > 0) {
            errors.forEach(error => {
                const key = error.path as keyof TForm;
                const field = fields[key];

                if (field !== undefined) {
                    field.setError(error.message);                    
                }
                else {
                    console.log(errors, fields);
                    throw new Error(`Campo ${key} não encontrado`);
                }
            });
        }
    }, [fields]);

    const setErrorsFromServer = React.useCallback((errors: IBadRequestError[]) => {
        if ((errors || []).length > 0) {
            errors.forEach(error => {
                const key = error.propertyName as keyof TForm;
                const field = fields[key];

                if (field !== undefined) {
                    field.setError(error.errorMessage);
                }
                else {
                    console.log({errors, fields});
                    throw new Error(`Campo ${key} não encontrado`);
                }
            });
        }
    }, [fields]);

    const saveValues = React.useCallback(() => {
        const values = getInputValues();
        setValues(values);
    }, [getInputValues, setValues]);

    const clearValues = React.useCallback(() => {
        setValues({} as TForm);
    }, [setValues]);

    const clearErrors = React.useCallback(() => {
        forEachField((field, fieldKeyOf) => {
            field.clearError();
        })
    }, [forEachField]);

    const reset = React.useCallback(() => {
        forEachField((field, fieldKeyOf) => {
            field.reset();
        })
    }, [forEachField]);

    const clear = React.useCallback(() => {
        clearValues();
        clearErrors();
    }, [clearErrors, clearValues]);

    const handleResponseSetValues = React.useCallback((response: AxiosResponse<TForm>) => {
        setValues(response.data);
    }, [setValues]);

    useEffectOnce(() => {
        forEachField((field, fieldKeyOf) => {
            field.setName(fieldKeyOf as string);
        })
    })
    
    return React.useMemo(() => ({
        fields: fields as Required<FormFields<TForm>>,
        
        setValues,
        getValues,
        clearValues,
        saveValues,
        handleResponseSetValues,
        
        setErrorsFromYup,
        setErrorsFromServer,
        clearErrors,
        
        reset,
        clear,

        //setFieldInputRef,
        getInputValues,
        forEachField

    }), [fields, setValues, getValues, clearValues, saveValues, handleResponseSetValues, setErrorsFromYup, setErrorsFromServer, clearErrors, reset, clear, getInputValues, forEachField]);
}